// Paste-import modal. Parses pasted TSV/CSV client-side and hands
// the rows back to the JobModal which appends them to the spool
// table draft. Server-side import endpoint exists too (used by the
// future auto-import flow); inside the modal, we keep the staging
// local so the user can preview before committing.
import { useState } from "react";
import {
  SPOOL_STATUSES,
  type SpoolInputT,
  type SpoolStatus,
} from "../../../../../../shared/types/production-schedule";

interface Props {
  open: boolean;
  onClose(): void;
  onImported(rows: SpoolInputT[]): void;
}

const PLACEHOLDER =
  "SP-001\tMain header\t16\t2026-07-01\t2026-07-02\t2026-07-03\t2026-07-05\tpending";

function parseStatus(s: string | undefined): SpoolStatus {
  const norm = (s ?? "").trim().toLowerCase();
  return (SPOOL_STATUSES as readonly string[]).includes(norm)
    ? (norm as SpoolStatus)
    : "pending";
}

function isoOrNull(raw: string | undefined): string | null {
  const v = (raw ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

function parsePastedText(text: string): SpoolInputT[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.map((line) => {
    const cols = line.split(/\t|,/).map((c) => c.trim());
    return {
      num: cols[0] ?? "",
      description: cols[1] ?? "",
      hours: Number(cols[2]) || 0,
      cutStart: isoOrNull(cols[3]),
      cutDone: isoOrNull(cols[4]),
      weldStart: isoOrNull(cols[5]),
      weldDone: isoOrNull(cols[6]),
      status: parseStatus(cols[7]),
      sortOrder: 0,
    };
  });
}

export function SpoolImportModal({ open, onClose, onImported }: Props) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<SpoolInputT[]>([]);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const doPreview = () => {
    setError(null);
    const trimmed = text.trim();
    if (!trimmed) {
      setError("No data pasted.");
      setPreview([]);
      return;
    }
    const rows = parsePastedText(trimmed);
    const invalid = rows.filter((r) => !r.num);
    if (invalid.length > 0) {
      setError(`Row(s) missing Spool # — paste at least the first column.`);
      setPreview([]);
      return;
    }
    setPreview(rows);
  };

  const confirm = () => {
    const rows = preview.length ? preview : parsePastedText(text.trim());
    if (rows.length === 0) {
      setError("Nothing to import.");
      return;
    }
    onImported(rows);
    setText("");
    setPreview([]);
  };

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <h2>Import Spools from CSV / Paste</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: "var(--bsp-fg-muted)", marginBottom: 12, lineHeight: 1.5 }}>
            Paste rows from Excel or CSV. Columns (in order):{" "}
            <strong style={{ color: "var(--bsp-dark)" }}>
              Spool#, Description, Hours, CutStart, CutDone, WeldStart, WeldDone, Status
            </strong>
            <br />
            Dates: YYYY-MM-DD. Status: pending/cutting/cut/welding/complete. Only Spool# is required.
          </p>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Paste data here</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={PLACEHOLDER}
              style={{ height: 160, fontFamily: "var(--bsp-font-mono)", fontSize: 11 }}
            />
          </div>
          {error && (
            <div style={{ color: "var(--bsp-red-fg)", fontWeight: 700, fontSize: 12 }}>{error}</div>
          )}
          {preview.length > 0 && (
            <div style={{ fontSize: 11 }}>
              <div style={{ color: "var(--bsp-green-fg)", fontWeight: 700 }}>
                Parsed {preview.length} row{preview.length === 1 ? "" : "s"}.
              </div>
              <div style={{ marginTop: 6, fontFamily: "var(--bsp-font-mono)", fontSize: 10 }}>
                {preview.slice(0, 3).map((s, i) => (
                  <div key={i}>{s.num} · {s.description ?? ""} · {s.hours}h</div>
                ))}
                {preview.length > 3 && <div>… and {preview.length - 3} more</div>}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-sm" onClick={doPreview} style={{ padding: "8px 14px" }}>Preview</button>
          <button className="btn-submit" onClick={confirm}>Import Spools</button>
        </div>
      </div>
    </div>
  );
}
